import { createFileRoute } from "@tanstack/react-router";
import { Server } from "lucide-react";
import { useMemo } from "react";
import { BackgroundGlow } from "~/components/background-glow";
import { GuildCard } from "~/components/guild-card";
import { getDashboardData } from "~/lib/overseer";
import type { GuildWithSubscription } from "~/lib/overseer.types";
import { LoadingSpinner } from "~/routes/__root";

function sortGuilds(guilds: GuildWithSubscription[]) {
  return [...guilds].sort((a, b) => {
    const getPriority = (g: GuildWithSubscription) => {
      if (g.subscription && g.owner) return 5;
      if (g.subscription) return 4;
      if (g.isBotInGuild && g.owner) return 3;
      if (g.isBotInGuild) return 2;
      if (g.owner) return 1;
      return 0;
    };
    return getPriority(b) - getPriority(a);
  });
}

export const Route = createFileRoute("/_authed/dashboard/")({
  pendingComponent: LoadingSpinner,
  pendingMs: 0,
  loader: async () => getDashboardData(),
  component: RouteComponent,
});

function RouteComponent() {
  const { guilds, subscriptionContext } = Route.useLoaderData();
  const visibleGuilds = useMemo(() => sortGuilds(guilds), [guilds]);

  return (
    <div className="min-h-screen">
      <BackgroundGlow />

      <div className="relative mx-auto max-w-6xl px-6 py-16">
        {/* Header */}
        <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-primary">
              Dashboard
            </p>
            <h1 className="text-3xl font-extrabold tracking-tight text-foreground">
              Select a Server
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Showing {visibleGuilds.length} server
              {visibleGuilds.length !== 1 ? "s" : ""} where you have admin
              access.
            </p>
          </div>
        </div>

        {visibleGuilds.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-border bg-card/30 py-24 text-center shadow-xl shadow-primary/5 backdrop-blur-md backdrop-saturate-150">
            <Server className="mb-4 h-10 w-10 text-muted-foreground/50" />
            <p className="font-semibold text-foreground">No servers found</p>
            <p className="mt-1 text-sm text-muted-foreground">
              You don't have admin access to any servers with Overseer
              installed.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {visibleGuilds.map((guild) => (
              <GuildCard
                key={guild.id}
                guild={guild}
                subscriptionContext={subscriptionContext}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
